import React from 'react'
import type { FC } from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import {
  useGetSummaryStatsQuery,
  useGetReviewsQuery,
  useGetAreasQuery,
  useGetV2DevelopmentsQuery,
  useGetAreaGuideBenchmarkQuery,
  useGetAreaGuidesQuery,
  useGetPageViewStatisticsQuery,
  useGetV2WidgetsQuery,
} from '@homeviews/store'
import { WindowTitle } from '@homeviews/shared-ui'
import {
  Typography,
  dateToday,
  dateAYearAgo,
  dateAQuarterAgo,
  ReputationManagement,
  MarketingCollateral,
  BrandAwareness,
} from '@homeviews/analytics'
import type {
  Reviews,
  MetaTotal,
  AreaGuideBenchmark,
  AreaGuides,
  PageViewStatistics,
  SummaryStats,
} from '@homeviews/analytics'

const useStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      padding: '24px 16px',
    },
    topHeaderWrapper: {
      display: 'flex',
      marginBottom: '18px',
    },
  }),
)

const commonUseQueryOptions = {
  // https://redux-toolkit.js.org/rtk-query/api/createApi#refetchonmountorargchange
  refetchOnMountOrArgChange: true,
}

export const Page: FC = () => {
  const classes = useStyles()

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: summaryAllTime, isLoading: summaryAllTimeLoading } =
    useGetSummaryStatsQuery(null, { refetchOnMountOrArgChange: true })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: summaryLastYear, isLoading: summaryLastYearLoading } =
    useGetSummaryStatsQuery(
      {
        date_from: dateAYearAgo(),
        date_to: dateToday(),
      },
      commonUseQueryOptions,
    )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: summaryLastQuarter, isLoading: summaryLastQuarterLoading } =
    useGetSummaryStatsQuery(
      {
        date_from: dateAQuarterAgo(),
        date_to: dateToday(),
      },
      commonUseQueryOptions,
    )

  const { data: reviews } = useGetReviewsQuery(
    {
      rating: 5,
      sort: 'date',
      sort_direction: 'desc',
    },
    commonUseQueryOptions,
  )

  const { data: dmentAreasInGuides, isLoading: dmentAreasInGuidesLoading } =
    useGetAreasQuery(
      {
        portalDevelopments: true,
        areaGuide: true,
      },
      commonUseQueryOptions,
    )

  const {
    data: dmentsTotalInTopGuides,
    isLoading: dmentsTotalInTopGuidesLoading,
  } = useGetV2DevelopmentsQuery(
    {
      portalDevelopments: true,
      areaRanking: '<11',
    },
    commonUseQueryOptions,
  )

  const { data: dmentsTotal, isLoading: dmentsTotalLoading } =
    useGetV2DevelopmentsQuery(
      {
        portalDevelopments: true,
      },
      commonUseQueryOptions,
    )

  const { data: guideBenchmark, isLoading: guideBenchmarkLoading } =
    useGetAreaGuideBenchmarkQuery(null, commonUseQueryOptions)

  const {
    data: dmentsTotalInRedScore,
    isLoading: dmentsTotalInRedScoreLoading,
  } = useGetV2DevelopmentsQuery(
    {
      portalDevelopments: true,
      transparencyAward: 4,
    },
    commonUseQueryOptions,
  )

  const { data: areaGuides } = useGetAreaGuidesQuery(
    {
      portalDevelopments: true,
    },
    commonUseQueryOptions,
  )

  const { data: pageViews, isLoading: pageViewsLoading } =
    useGetPageViewStatisticsQuery(
      {
        portalDeveloper: true,
        dateFrom: dateAYearAgo(),
        dateTo: dateToday(),
      },
      commonUseQueryOptions,
    )

  const { data: widgets, isLoading: widgetsLoading } = useGetV2WidgetsQuery(
    {
      portalDeveloper: true,
      lastViewDate: `>${dateAYearAgo()}`,
    },
    commonUseQueryOptions,
  )

  return (
    <div className={classes.wrapper}>
      <WindowTitle title="Analytics" />
      <div className={classes.topHeaderWrapper}>
        <Typography variant="h1">Analytics</Typography>
        <Typography variant="subtitle1">
          Your dashboard shows data for the profiles you have been granted
          access to.
        </Typography>
      </div>
      <ReputationManagement
        summaryAllTimeLoading={summaryAllTimeLoading}
        summaryAllTime={summaryAllTime as SummaryStats}
        summaryLastYearLoading={summaryLastYearLoading}
        summaryLastYear={summaryLastYear as SummaryStats}
        summaryLastQuarterLoading={summaryLastQuarterLoading}
        summaryLastQuarter={summaryLastQuarter as SummaryStats}
      />
      <MarketingCollateral
        reviews={reviews as Reviews}
        dmentAreasInGuidesLoading={dmentAreasInGuidesLoading}
        dmentAreasInGuides={dmentAreasInGuides as Required<MetaTotal>}
        dmentsTotalInTopGuidesLoading={dmentsTotalInTopGuidesLoading}
        dmentsTotalInTopGuides={dmentsTotalInTopGuides as Required<MetaTotal>}
        dmentsTotalLoading={dmentsTotalLoading}
        dmentsTotal={dmentsTotal as Required<MetaTotal>}
        guideBenchmarkLoading={guideBenchmarkLoading}
        guideBenchmark={guideBenchmark as Required<AreaGuideBenchmark>}
        dmentsTotalInRedScoreLoading={dmentsTotalInRedScoreLoading}
        dmentsTotalInRedScore={dmentsTotalInRedScore as Required<MetaTotal>}
        areaGuides={areaGuides as Required<AreaGuides>}
      />
      <BrandAwareness
        pageViewsLoading={pageViewsLoading}
        pageViews={pageViews as Required<PageViewStatistics>}
        widgetsLoading={widgetsLoading}
        widgets={widgets as Required<MetaTotal>}
      />
    </div>
  )
}
